import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Toast, type ToastVariant } from "@/components/ui/toast/toast";

interface ToastData {
	id: string;
	variant: ToastVariant;
	title: string;
	description?: string;
}

interface ToastContextValue {
	showToast: (toast: Omit<ToastData, "id">) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
};

interface ToastProviderProps {
	children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
	const [toasts, setToasts] = useState<ToastData[]>([]);
	const timeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
		if (timeoutsRef.current[id]) {
			clearTimeout(timeoutsRef.current[id]);
			delete timeoutsRef.current[id];
		}
	}, []);

	const showToast = useCallback(
		(toast: Omit<ToastData, "id">) => {
			const exists = toasts.find(
				(t) => t.title === toast.title && t.variant === toast.variant
			);
			if (exists) return;

			const id = crypto.randomUUID();
			const newToast = { ...toast, id };

			setToasts((prev) => [...prev, newToast]);

			const timeout = setTimeout(() => {
				removeToast(id);
			}, 6000);

			timeoutsRef.current[id] = timeout;
		},
		[toasts, removeToast]
	);

	useEffect(
		() => () => {
			for (const timeout of Object.values(timeoutsRef.current)) {
				clearTimeout(timeout);
			}
		},
		[]
	);

	const value = useMemo(() => ({ showToast }), [showToast]);

	const renderToast = useCallback(
		(toast: ToastData) => (
			<div
				className="pointer-events-auto animate-slide-in-from-right"
				key={toast.id}
			>
				<Toast
					description={toast.description}
					onClose={() => removeToast(toast.id)}
					title={toast.title}
					variant={toast.variant}
				/>
			</div>
		),
		[removeToast]
	);

	return (
		<ToastContext.Provider value={value}>
			{children}
			<div
				aria-live="polite"
				className="item-center pointer-events-none fixed top-6 right-6 z-[30000] flex flex-col gap-3"
			>
				{toasts.map(renderToast)}
			</div>
		</ToastContext.Provider>
	);
};
